import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { netappAPI, type NetAppConfig } from '@/lib/api/netapp';

interface ConfigurationWizardProps {
  config?: NetAppConfig;
  onClose: () => void;
  onComplete: () => void;
}

export function ConfigurationWizard({ config, onClose, onComplete }: ConfigurationWizardProps) {
  const [name, setName] = useState(config?.name || 'NetApp AIQ');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const data = { name: name.trim(), ...(token.trim() ? { refresh_token: token.trim() } : {}) };
      if (config) await netappAPI.updateConfig(config.id, data);
      else await netappAPI.createConfig({ name: name.trim(), refresh_token: token.trim() });
      setToken('');
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Unable to save the AIQ connection. Check the token and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <section role="dialog" aria-modal="true" aria-labelledby="aiq-connection-title" className="w-full max-w-lg rounded-xl bg-white dark:bg-slate-900 p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 id="aiq-connection-title" className="text-xl font-semibold">{config ? 'Update AIQ connection' : 'Connect to NetApp AIQ'}</h2>
          <button onClick={onClose} disabled={saving} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">One connection discovers all customers available to your token. Data is collected automatically in the background.</p>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label htmlFor="aiq-name" className="block text-sm font-medium mb-1">Connection name</label>
            <input id="aiq-name" required maxLength={255} value={name} onChange={e => setName(e.target.value)} className="w-full border rounded-lg p-2 dark:bg-slate-800 dark:border-slate-600" />
          </div>
          <div>
            <label htmlFor="aiq-token" className="block text-sm font-medium mb-1">{config ? 'Replacement refresh token (optional)' : 'AIQ refresh token'}</label>
            <div className="flex gap-2">
              <input id="aiq-token" type={showToken ? 'text' : 'password'} required={!config} autoComplete="off" value={token} onChange={e => setToken(e.target.value)} className="w-full border rounded-lg p-2 dark:bg-slate-800 dark:border-slate-600" />
              <button type="button" onClick={() => setShowToken(!showToken)} aria-label={showToken ? 'Hide token' : 'Show token'}>{showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
            </div>
            {config && <p className="text-xs text-gray-500 mt-1">Leave blank to keep the saved token.</p>}
          </div>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" disabled={saving} onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
            <button type="submit" disabled={saving || !name.trim() || (!config && !token.trim())} className="px-4 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50">{saving ? 'Connecting…' : config ? 'Save connection' : 'Connect and collect customers'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
